import { LifeInsuranceRatesFilter, PendingResponse } from 'embed/constants'
import { CommonState } from 'embed/reducers'
import { RatesState, RateTableWithRequestTimestamp, SortedState } from 'embed/reducers/rates'
import { DepositsOffer, LifeInsuranceOffer, LoanOffer, SpecialOffer } from 'embed/constants/models'
import { CreditSortOptionUI, Product, SortDirection } from '@evenfinancial/even-ts-static'
import { DeepReadonly } from 'utility-types'

export const ratesFiltersSelector = (state: CommonState) => state.rates.filters
export const ratesStateSelector = (state: CommonState): RatesState => state.rates
export const ratesSortedOffersSelector = (state: CommonState): SortedState => state.rates.sorted
export const ratesMaxResultsSelector = (state: CommonState, product: Product): number | undefined =>
  ratesStateSelector(state).maxResults[product]
const ratesSortDirectionSelector = (state: CommonState) => ratesStateSelector(state).sortDirection
const ratesSortOptionSelector = (state: CommonState) => ratesStateSelector(state).sortOption
export const ratesPendingResponsesSelector = (state: CommonState, product: Product) =>
  ratesStateSelector(state)?.[product].pendingResponses

export const lifeInsuranceRatesStateSelector = (state: CommonState): RateTableWithRequestTimestamp =>
  ratesStateSelector(state).life_insurance
export const lifeInsuranceRatesOffersSelector = (state: CommonState): DeepReadonly<LifeInsuranceOffer[]> =>
  lifeInsuranceRatesStateSelector(state).lifeInsuranceOffers
export const lifeInsuranceRatesLeadUuidSelector = (state: CommonState) =>
  lifeInsuranceRatesStateSelector(state).leadUuid
export const lifeInsuranceRatesUuidSelector = (state: CommonState) => lifeInsuranceRatesStateSelector(state).uuid
export const ratesFilteredLifeInsuranceOffersSelector = (
  rates: LifeInsuranceOffer[] = [],
  ratesFilters: DeepReadonly<{ [key in string | number | symbol]: string[] }> = {},
) => {
  for (const filterKey in ratesFilters) {
    if (filterKey === LifeInsuranceRatesFilter.CoverageTier) {
      const filteredRates = rates
        .filter((offer) => offer.details.packageType && ratesFilters[filterKey].includes(offer.details.packageType))
        .sort((a, b) => a.details.monthlyPremiumAmount - b.details.monthlyPremiumAmount)
        .sort((a, b) => (b.recommendationScore ?? 0) - (a.recommendationScore ?? 0))
      const recommendedFilteredRates = filteredRates.filter((offer) => offer.details.recommended)
      const notRecommendedFilteredRates = filteredRates.filter((offer) => !offer.details.recommended)
      return [...recommendedFilteredRates, ...notRecommendedFilteredRates]
    }
  }
  return rates
}
export const lifeInsuranceOfferSelectedSelector = (state: CommonState) =>
  ratesStateSelector(state).selected.life_insurance

export const loanOfferSelectedSelector = (state: CommonState) => ratesStateSelector(state).selected.loan

export const loanRatesFiltersSelector = (state: CommonState) => ratesFiltersSelector(state).loan
export const loanRatesStateSelector = (state: CommonState): RateTableWithRequestTimestamp =>
  ratesStateSelector(state).loan
export const loanSpecialOffersSelector = (state: CommonState): DeepReadonly<SpecialOffer[]> =>
  loanRatesStateSelector(state).specialOffers
export const loanOffersSelector = (state: CommonState): DeepReadonly<LoanOffer[]> =>
  loanRatesStateSelector(state).loanOffers
export const loanRatesLeadUuidSelector = (state: CommonState) => loanRatesStateSelector(state).leadUuid
export const loanRatesUuidSelector = (state: CommonState) => loanRatesStateSelector(state).uuid
export const loanAmountStateSelector = (state: CommonState): number => loanRatesStateSelector(state).loanAmount ?? 0
export const loanRatesSortOptionSelector = (state: CommonState) => ratesSortOptionSelector(state).loan
export const loanRatesSortDirectionSelector = (state: CommonState) => ratesSortDirectionSelector(state).loan

const loanPendingResponsesSelector = (state: CommonState): DeepReadonly<PendingResponse[]> =>
  loanRatesStateSelector(state).pendingResponses ?? []

export const loanExclusivelySpecialOffersSelector = (state: CommonState): boolean =>
  loanSpecialOffersSelector(state).length > 0 &&
  loanOffersSelector(state).length === 0 &&
  loanPendingResponsesSelector(state).length === 0

export const loanAreAllOffersEmptySelector = (state: CommonState): boolean =>
  loanSpecialOffersSelector(state).length === 0 &&
  loanOffersSelector(state).length === 0 &&
  loanPendingResponsesSelector(state).length === 0

export const ratesUuidSelector = (state: CommonState, product: Product) => ratesStateSelector(state)[product].uuid

export const depositsRatesStateSelector = (state: CommonState): RateTableWithRequestTimestamp =>
  ratesStateSelector(state).deposits
export const depositsSavingsOffersSelector = (state: CommonState): DeepReadonly<DepositsOffer[]> =>
  depositsRatesStateSelector(state).savingsOffers
export const depositsSortedSavingsOffersSelector = (state: CommonState) =>
  ratesSortedOffersSelector(state).deposits?.savingsOffers ?? []
export const creditCardRatesStateSelector = (state: CommonState): RateTableWithRequestTimestamp =>
  ratesStateSelector(state).credit_card
export const creditCardRatesOffersSelector = (state: CommonState) =>
  creditCardRatesStateSelector(state).creditCardOffers
export const creditCardRatesUuidSelector = (state: CommonState): string => creditCardRatesStateSelector(state).uuid
export const creditCardRatesSortOptionSelector = (state: CommonState): CreditSortOptionUI =>
  ratesSortOptionSelector(state).credit_card
export const creditCardRatesSortDirectionSelector = (state: CommonState): SortDirection =>
  ratesSortDirectionSelector(state).credit_card
